import { useState } from 'react';
import { FormattedMessage } from 'react-intl';

import { ExclamationIcon } from '@/components/icons/exclamation';
import { Button } from '@/components/ui/button';
import { Typography } from '@/components/ui/typography';

import { MAX_INELIGIBLE_OPENINGS_COUNT } from './constant';
import { IneligibleOpening, IneligibleOpeningReason } from './types';

interface IneligibleOpeningsBannerProps {
  ineligibleOpenings: IneligibleOpening[];
}

function getReasonLabel(reason: IneligibleOpeningReason) {
  switch (reason) {
    case 'inactive':
      return <FormattedMessage defaultMessage="(Inactive)" />;
    case 'sourcing_disabled':
      return <FormattedMessage defaultMessage="(Sourcing Disabled)" />;
    default:
      return null;
  }
}

/**
 * Banner shown when selected openings include inactive or sourcing-disabled openings
 * that won't be posted to job boards.
 */
export function IneligibleOpeningsBanner({
  ineligibleOpenings,
}: IneligibleOpeningsBannerProps) {
  const [isExpanded, setIsExpanded] = useState(false);

  if (ineligibleOpenings.length === 0) return null;

  const visibleOpenings = isExpanded
    ? ineligibleOpenings
    : ineligibleOpenings.slice(0, MAX_INELIGIBLE_OPENINGS_COUNT);
  const hasMore =
    ineligibleOpenings.length > MAX_INELIGIBLE_OPENINGS_COUNT && !isExpanded;

  return (
    <>
      <div className="bg-fx-rose-50 flex items-center gap-2 self-stretch rounded-md p-2">
        <div className="flex grow flex-col items-start justify-center">
          <div className="flex flex-col gap-2">
            <div className="flex items-start gap-2">
              <ExclamationIcon className="text-fx-rose mt-0.5 shrink-0" />
              <Typography variant="body1">
                <FormattedMessage
                  defaultMessage="{count, plural, one {# selected opening isn't eligible for posting and will be excluded.} other {# selected openings aren't eligible for posting and will be excluded.}}"
                  values={{ count: ineligibleOpenings.length }}
                />
              </Typography>
            </div>
            <div className="pl-7">
              <ul className="ml-4 list-disc">
                {visibleOpenings.map(opening => (
                  <li key={opening.externalId} className="leading-5">
                    <Typography variant="body1">
                      {opening.title} {getReasonLabel(opening.reason)}
                    </Typography>
                  </li>
                ))}
                {hasMore && (
                  <li className="leading-5">
                    <Button
                      variant="text"
                      size="link"
                      className="!text-fx-rose h-auto !p-0 text-sm font-normal"
                      onClick={() => setIsExpanded(true)}
                    >
                      <FormattedMessage defaultMessage="View more" />
                    </Button>
                  </li>
                )}
                {isExpanded &&
                  ineligibleOpenings.length > MAX_INELIGIBLE_OPENINGS_COUNT && (
                    <li className="leading-5">
                      <Button
                        variant="text"
                        size="link"
                        className="!text-fx-rose h-auto !p-0 text-sm font-normal"
                        onClick={() => setIsExpanded(false)}
                      >
                        <FormattedMessage defaultMessage="View less" />
                      </Button>
                    </li>
                  )}
              </ul>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
